import { useState } from 'react';
import type { CalendarEvent } from '../types';
import { supabase } from '../supabase';
import './EventDetailModal.css';

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatRange(start: string, end: string) {
  const s = new Date(start);
  const e = new Date(end);
  if (s.toDateString() === e.toDateString()) return formatDate(start);
  return `${formatDate(start)} – ${formatDate(end)}`;
}

const categoryClass: Record<string, string> = {
  cultural: 'category-cultural',
  sporting: 'category-sporting',
  music: 'category-music',
  industry: 'category-industry',
};

interface EventDetailModalProps {
  event: CalendarEvent;
  isSaved: boolean;
  onToggleSaved: () => void;
  onClose: () => void;
}

export function EventDetailModal({ event, isSaved, onToggleSaved, onClose }: EventDetailModalProps) {
  const [email, setEmail] = useState('');
  const [reminderStatus, setReminderStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const slug = (event.category as { slug?: string })?.slug ?? '';
  const categoryClassname = categoryClass[slug] ?? '';
  const categoryName = (event.category as { name?: string })?.name ?? 'Event';

  const handleReminderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setReminderStatus('loading');
    const { error } = await (supabase as any).from('event_reminders').insert({ event_id: event.id, email: trimmed });
    if (error) {
      setReminderStatus('error');
      return;
    }
    setReminderStatus('success');
    setEmail('');
  };

  return (
    <div className="event-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="event-modal-title">
      <div className={`event-modal event-modal--${slug} ${categoryClassname}`} onClick={(e) => e.stopPropagation()}>
        <div className="event-modal-accent" aria-hidden="true" />
        <button type="button" className="event-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <div className="event-modal-body">
          <div className="event-modal-meta">
            <span className="event-modal-category">{categoryName}</span>
            <span className="event-modal-dates">{formatRange(event.start_date, event.end_date)}</span>
          </div>
          <h2 id="event-modal-title" className="event-modal-title">{event.title}</h2>
          {event.location && (
            <p className="event-modal-location">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              {event.location}
            </p>
          )}
          {event.description && (
            <div className="event-modal-description">{event.description}</div>
          )}
          {event.host && (event.host as { name?: string }).name && (
            <p className="event-modal-host">Host: {(event.host as { name: string }).name}</p>
          )}

          <div className="event-modal-actions">
            <button
              type="button"
              className={`event-modal-save ${isSaved ? 'is-saved' : ''}`}
              onClick={onToggleSaved}
            >
              {isSaved ? '✓ Saved for later' : 'Save for later'}
            </button>
            {event.external_url && (
              <a
                href={event.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="event-modal-external"
              >
                Visit event site →
              </a>
            )}
          </div>

          <div className="event-modal-reminder">
            <h3 className="event-modal-reminder-title">Remind me 1 day before</h3>
            <p className="event-modal-reminder-desc">We’ll email you the day before this event.</p>
            {reminderStatus === 'success' ? (
              <p className="event-modal-reminder-success">You’re on the list. We’ll email you the day before.</p>
            ) : (
              <form onSubmit={handleReminderSubmit} className="event-modal-reminder-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="event-modal-reminder-input"
                  disabled={reminderStatus === 'loading'}
                  required
                />
                <button type="submit" className="event-modal-reminder-btn" disabled={reminderStatus === 'loading'}>
                  {reminderStatus === 'loading' ? 'Adding…' : 'Remind me'}
                </button>
              </form>
            )}
            {reminderStatus === 'error' && (
              <p className="event-modal-reminder-error">Something went wrong. Try again.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
